// src/screens/CheckInScreen.js
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { appointmentsAPI } from '../services/api';

const CheckInScreen = () => {
  const { token } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [appointment, setAppointment] = useState(null);

  useEffect(() => {
    // If user is not logged in, redirect to login with return URL
    if (!user) {
      navigate(`/login?redirect=/checkin/${token}`);
      return;
    }

    // If user is logged in, attempt check-in
    handleCheckIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const handleCheckIn = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await appointmentsAPI.checkIn(token);

      setAppointment(response.data.appointment);
      setSuccess(true);
    } catch (err) {
      console.error('Check-in error:', err);
      const msg = err.response?.data?.message || 'Failed to check in';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (user.role === 'kiosk') {
      navigate('/kiosk');
    } else if (user.role === 'dealer_admin' || user.role === 'dealer_staff') {
      navigate('/dealer');
    } else if (user.role === 'admin') {
      navigate('/admin/dealers');
    } else {
      navigate('/appointments');
    }
  };

  return (
    <div className="flex flex-col h-full bg-app-bg-light dark:bg-black text-app-text dark:text-white">
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {loading && (
          <div className="text-center space-y-4">
            <div className="text-4xl">⏳</div>
            <div className="text-lg font-semibold">Checking in...</div>
          </div>
        )}

        {error && (
          <div className="w-full max-w-md space-y-4">
            <div className="text-center space-y-4">
              <div className="text-4xl">❌</div>
              <div className="text-xl font-bold">Check-In Failed</div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            </div>

            <button
              onClick={handleContinue}
              className="w-full py-3 rounded-2xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-sm font-semibold hover:opacity-90"
            >
              Go Back
            </button>
          </div>
        )}

        {success && appointment && (
          <div className="w-full max-w-md space-y-4">
            <div className="text-center space-y-4">
              <div className="text-5xl">✅</div>
              <div className="text-2xl font-bold">Successfully Checked In!</div>

              <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-4 border border-app-border-light dark:border-app-border space-y-3 text-left">
                <div className="text-sm font-semibold text-app-accent mb-2">
                  Appointment Details
                </div>

                <div>
                  <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Customer</div>
                  <div className="text-sm text-app-text-light dark:text-app-text mt-1">
                    {appointment.user?.name}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Date & Time</div>
                  <div className="text-sm text-app-text-light dark:text-app-text mt-1">
                    {new Date(appointment.date).toLocaleDateString()} at {appointment.time}
                  </div>
                </div>

                {appointment.vehicles && appointment.vehicles.length > 0 && (
                  <div>
                    <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Vehicles</div>
                    <div className="text-sm text-app-text-light dark:text-app-text mt-1">
                      {appointment.vehicles.map((v) => (
                        <div key={v._id}>
                          {v.year} {v.make} {v.model}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Status</div>
                  <div className="text-sm text-app-accent mt-1 font-semibold">
                    {appointment.status}
                  </div>
                </div>

                {appointment.checkInTime && (
                  <div>
                    <div className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Check-In Time</div>
                    <div className="text-sm text-app-text-light dark:text-app-text mt-1">
                      {new Date(appointment.checkInTime).toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={handleContinue}
              className="w-full py-3 rounded-2xl bg-app-accent text-black text-sm font-semibold hover:opacity-90"
            >
              Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckInScreen;
